import React from 'react';
import { Highlighter, Underline, Strikethrough, Square, Circle, X, MessageSquare, Pen, type LucideProps } from 'lucide-react';
import type { AnnotationTool } from '../constants/signing';

export interface AnnotationToolbarProps {
  /** Currently active annotation tool, or null when none is selected. */
  activeTool: string | null;
  /** Called when the user selects or deselects a tool.
   *  Emits an annotation tool id, or null when toggled off. */
  onSelectTool(tool: string | null): void;
}

interface ToolDef {
  id: AnnotationTool;
  label: string;
  title: string;
  Icon: React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;
  /** Tailwind accent used for the active state ring. */
  activeRing: string;
  activeBg: string;
  activeText: string;
}

const TOOLS: ToolDef[] = [
  {
    id: 'highlight',
    label: 'Highlight',
    title: 'Highlight tool — draw a semi-transparent colored rectangle over text',
    Icon: Highlighter,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-muted',
    activeText: 'text-accent dark:text-accent',
  },
  {
    id: 'underline',
    label: 'Underline',
    title: 'Underline annotation — place a horizontal underline line over text',
    Icon: Underline,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-muted',
    activeText: 'text-accent dark:text-accent',
  },
  {
    id: 'strikeout',
    label: 'Strikeout',
    title: 'Strikeout annotation — place a horizontal strikethrough line over text',
    Icon: Strikethrough,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-muted',
    activeText: 'text-accent dark:text-accent',
  },
  {
    id: 'rect',
    label: 'Rectangle',
    title: 'Rectangle annotation — draw an outlined rectangle',
    Icon: Square,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-muted',
    activeText: 'text-accent dark:text-accent',
  },
  {
    id: 'ellipse',
    label: 'Circle',
    title: 'Circle annotation — draw an outlined ellipse/circle',
    Icon: Circle,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-soft',
    activeText: 'text-accent dark:text-accent-muted',
  },
  {
    id: 'cross',
    label: 'Cross',
    title: 'Cross mark annotation — place an X mark',
    Icon: X,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-soft',
    activeText: 'text-accent dark:text-accent-muted',
  },
  {
    id: 'text-bubble',
    label: 'Bubble',
    title: 'Text bubble annotation — place an editable callout',
    Icon: MessageSquare,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-soft',
    activeText: 'text-accent dark:text-accent-muted',
  },
  {
    id: 'ink',
    label: 'Pen',
    title: 'Freehand pen — drag to draw a freehand stroke',
    Icon: Pen,
    activeRing: 'ring-accent',
    activeBg: 'bg-accent-muted dark:bg-accent-soft',
    activeText: 'text-accent dark:text-accent-muted',
  },
];

/**
 * Small annotation tool segment. Clicking a tool activates it; clicking the
 * active tool clears the selection (toggle behaviour). The wiring agent routes
 * the selected tool into SigningWorkspace's `currentTool` state.
 *
 * Props match the WIRING_INTEGRATION contract exactly.
 */
export const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  activeTool,
  onSelectTool,
}) => {
  return (
    <div
      className="flex items-center gap-0.5 rounded-xl border border-[var(--border-hairline)] bg-[var(--bg-panel)] px-1 py-1 shadow-sm select-none"
      role="toolbar"
      aria-label="Annotation tools"
    >
      {TOOLS.map((tool) => {
        const isActive = activeTool === tool.id;
        return (
          <button
            key={tool.id}
            onClick={() => onSelectTool(isActive ? null : tool.id)}
            title={tool.title}
            aria-label={tool.title}
            aria-pressed={isActive}
            className={[
              'flex flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
              isActive
                ? `${tool.activeBg} ${tool.activeText} ring-2 ${tool.activeRing}`
                : 'hover:bg-[var(--bg-stage)] text-[var(--text-chrome)]',
            ].join(' ')}
          >
            <tool.Icon size={15} />
            <span className="text-[9px] leading-none font-medium tracking-wide">
              {tool.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
